import React, { useState, useEffect } from 'react';
import coursesData from './courses-data.json';
import Quiz from './Quiz';
import './courses.css';

const CourseDetailPage = ({ courseId, onBack, onPurchase }) => {
  const course = coursesData.find((c) => c.id === courseId);
  const firstModuleId = course?.modules?.[0]?.id;

  // Estado inicial: abre las lecciones del primer módulo por defecto.
  const [openSection, setOpenSection] = useState({ moduleId: firstModuleId, type: 'lessons' });
  const [isPurchased, setIsPurchased] = useState(false);
  const [quizProgress, setQuizProgress] = useState({});

  useEffect(() => {
    try {
      const purchasedCourses = JSON.parse(localStorage.getItem('purchasedCourses')) || {};
      setIsPurchased(course?.price === 0 || purchasedCourses[courseId] === true);
    } catch {
      setIsPurchased(false);
    }
    try {
        const savedProgress = JSON.parse(localStorage.getItem('quizProgress')) || {};
        setQuizProgress(savedProgress);
    } catch {
        setQuizProgress({});
    }
  }, [courseId, course?.price]);

  if (!course) {
    return (
      <div className="page-container">
        <h1 className="page-title">Curso no encontrado</h1>
        <button onClick={onBack} className="main-button secondary-button">Volver a los cursos</button>
      </div>
    );
  }
  
  const handleToggleSection = (moduleId, type) => {
    if (openSection.moduleId === moduleId && openSection.type === type) {
      setOpenSection({ moduleId: null, type: null }); // Cierra si se hace clic de nuevo
    } else {
      setOpenSection({ moduleId, type }); // Abre la sección solicitada
    }
  };

  const handleQuizComplete = (moduleId, progress) => {
      const newQuizProgress = { ...quizProgress, [moduleId]: progress };
      setQuizProgress(newQuizProgress);
      localStorage.setItem('quizProgress', JSON.stringify(newQuizProgress));
      if (progress.passed) {
        setOpenSection({ moduleId: null, type: null }); // Cierra el quiz al pasarlo
      }
  }

  const isPaidAndLocked = course.price > 0 && !isPurchased;

  // eslint-disable-next-line no-unused-vars
  const Rating = ({ rating }) => (
    <div style={{ color: '#fcd34d' }}>{'★'.repeat(rating)}{'☆'.repeat(5 - rating)}</div>
  );

  return (
    <div className="page-container course-detail-page">
      <div className="course-detail-header-no-image">
        <div className="course-detail-header-content">
          <span className="course-card-level">{course.level}</span>
          <h1 className="page-title" style={{ fontFamily: 'Cinzel, serif' }}>{course.title}</h1>
          <p className="page-subtitle">{course.tagline}</p>
        </div>
      </div>

      <div className="course-content-layout">
        <div className="course-detail-info"><p className="course-detail-description">{course.description}</p><div className="course-detail-meta"><div className="course-meta-item"><span>📚</span><span>{course.totalModules} Módulos</span></div><div className="course-meta-item"><span>📖</span><span>{course.totalLessons} Lecciones</span></div>{course.duration && (<div className="course-meta-item"><span>⏱️</span><span>{course.duration}</span></div>)}</div></div>
        <div className="value-proposition-box"><h4>⭐ Propuesta de Valor Única</h4><p>{course.propuestaDeValor}</p></div>
        <div className="course-info-grid"><div className="what-you-will-learn"><h3 style={{ color: '#fcd34d' }}>✨ Qué Aprenderás</h3><ul>{course.whatYouWillLearn.map((item, index) => (<li key={index}>✓ {item}</li>))}</ul></div><div className="requirements"><h3 style={{ color: '#fcd34d' }}>📋 Requisitos</h3><ul>{course.requirements.map((item, index) => (<li key={index}>• {item}</li>))}</ul></div></div>

        <h2 className="modules-title">Contenido del Curso</h2>
        <div className="course-modules-list">
          {course.modules.map((module, index) => {
            const isLocked = index > 0 && (!course.modules[index - 1].quiz || !quizProgress[course.modules[index - 1].id]?.passed);
            const isQuizPassed = quizProgress[module.id]?.passed;
            const tooltipMessage = isLocked ? "Debes aprobar la evaluación del módulo anterior para continuar." : "";
            
            return (
              <div key={index} className="module-item">
                <div className="module-header-new">
                  <div className="module-info">
                    <h3 className="module-title">{isLocked && '🔒 '}{module.title}</h3>
                    <p className="module-description">{module.description}</p>
                  </div>
                  <div className="module-actions">
                    <button 
                      onClick={() => handleToggleSection(module.id, 'lessons')}
                      disabled={isLocked}
                      className="main-button secondary-button small-button"
                      title={tooltipMessage} // <-- MENSAJE EXPLICATIVO AÑADIDO
                    >
                      Ver Lecciones
                    </button>
                    {module.quiz && (
                      <button 
                        onClick={() => handleToggleSection(module.id, 'quiz')}
                        disabled={isLocked}
                        className={`main-button small-button ${isQuizPassed ? 'quiz-passed-button' : 'primary-button'}`}
                        title={tooltipMessage} // <-- MENSAJE EXPLICATIVO AÑADIDO
                      >
                        {isQuizPassed ? 'Evaluación Aprobada ✔️' : 'Hacer Evaluación'}
                      </button>
                    )}
                  </div>
                </div>

                {openSection.moduleId === module.id && openSection.type === 'lessons' && (
                  <div className="lessons-container">
                    {module.lessons.map((lesson, lessonIndex) => (
                      <div key={lessonIndex} className="lesson-item">
                        <div className="lesson-header"><h4>{lesson.title}</h4><span className="lesson-duration">{lesson.duration}</span></div>
                        <div className="lesson-content">
                          {isPaidAndLocked ? (<div className="lesson-locked"><p>🔒 Esta lección es parte del curso premium. ¡Inscríbete para desbloquear todo el contenido!</p><button onClick={() => onPurchase(course)} className="main-button primary-button">Comprar por ${course.price} USD{course.originalPrice && (<span className="original-price-inline">${course.originalPrice}</span>)}</button></div>) 
                          : (<div dangerouslySetInnerHTML={{ __html: lesson.content }} />)}
                        </div>
                        {lesson.exercises && !isPaidAndLocked && (<div className="exercises-section">{lesson.exercises.map((exercise, exIndex) => (<div key={exIndex} className="exercise"><h4>🎯 {exercise.title}</h4><p>{exercise.description}</p></div>))}</div>)}
                      </div>
                    ))}
                  </div>
                )}

                {openSection.moduleId === module.id && openSection.type === 'quiz' && (
                   <div className="quiz-wrapper">
                    {!isPaidAndLocked && module.quiz && <Quiz module={module} onQuizComplete={handleQuizComplete} />}
                   </div>
                )}
              </div>
            )
          })}
        </div>

        <div className="instructor-card-no-image"><div><h3 style={{ margin: 0, color: '#fcd34d' }}>{course.instructor.name}</h3><p style={{ margin: '0.5rem 0 0 0', color: '#c4b5fd' }}>{course.instructor.bio}</p></div></div>
        
      </div>

      <button onClick={onBack} className="main-button secondary-button" style={{ marginTop: '3rem' }}>← Volver a todos los Cursos</button>
    </div>
  );
};

export default CourseDetailPage;